import { Actions, ControlProps } from "@jsonforms/core"
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react"
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import {
  getFormByName,
  getFormsByType,
} from "@/core/app/slices/uassist/form.util"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { RootState } from "../../../core/app/store"
import InputSummary from "../NewForm/InputSummary"
import { useFormContext } from "@/components/Form/FormContext"
import useDebouncedHandleChange from "../useDebouncedHandleChange"
import { FormType } from "@/components/Form/types"

const UTemplateSelectV2: FC<ControlProps> = (props) => {
  const { label, path, schema, data, handleChange } = props
  const [value, setValue] = useState(schema.default || "")
  const [selectedTemplate, setSelectedTemplate] = useState<{
    value: string
    id: string
  }>()

  const formContext = useFormContext()
  const { patientId, caseId } = useParams()
  const { core, dispatch: jsonFormDispatch } = useJsonForms()
  const [errors, setErrors] = useState("")
  const dispatch = useAppDispatch()
  const [inputValue, setInputValue] = useState("")
  const { formTemplateList, rxPrescription, rxJsonForm } = useAppSelector(
    (state: RootState) => state.formService,
  )
  const debouncedObject = useDebouncedHandleChange(data, handleChange)

  useEffect(() => {
    setValue(data)
  }, [data])


  const updateErrors = (message: string) => {
    setErrors(message)
    jsonFormDispatch &&
      jsonFormDispatch(
        Actions.updateErrors([
          {
            instancePath: "/" + path,
            message,
            schemaPath: "",
            keyword: "",
            params: {},
          },
        ]),
      )
  }

  const getTemplate = (formname: string) => {
    return getFormByName(formTemplateList, FormType.SMARTRX, formname)
  }

  useEffect(() => {
    if (core?.schema.required?.indexOf(path) === -1) return
    updateErrors(!value ? "This is a required field" : "")

    if (value) {
      const selectedForm = getTemplate(value)
      setSelectedTemplate({
        value: selectedForm.name,
        id: selectedForm.id,
      })
    }
  }, [data, value, formTemplateList, core?.validationMode])

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const selectedForm = getTemplate(event.target.value)
    setValue(event.target.value as string)
    handleChange(path, event.target.value)
    const templateIdAndValuePair = {
      value: selectedForm.name,
      id: selectedForm.id,
    }
    setSelectedTemplate(templateIdAndValuePair)
    selectedForm && handleChange(path + "_id", templateIdAndValuePair)
    formContext.fetchTemplate(selectedForm)
  }

  return (
    <>
      {schema?.summary ? (
        <>
          <InputSummary label={label} inputVal={data} />
          {/* <InputSummary label={value + " Instructions"} inputVal={inputValue} /> */}
        </>
      ) : (
        <>
        <Box sx={{ my: 1 }}>
          {label && (
            <InputLabel
              sx={{
                mb: 1,
                color: "rgba(0, 0, 0, 0.60)",
                "&.Mui-focused": {
                  color: "rgba(0, 0, 0, 0.60)",
                },
              }}
              error={!!(errors && core?.validationMode === "ValidateAndShow" && !value)}
              id={`${path}-label}`}
            >
              {label}
            </InputLabel>
          )}
          <FormControl
            fullWidth
            error={!!(errors && core?.validationMode === "ValidateAndShow" && !value)}
          >
            <Select
              labelId={`${path}-label}`}
              id={`${path}-select}`}
              value={value || ""}
              onChange={handleChangeSelect}
              renderValue={
                value
                  ? undefined
                  : () => (
                      <Box component={"div"} sx={{ color: "#aaa" }}>
                        {"Select"}
                      </Box>
                    )
              }
              displayEmpty
            >
              {formTemplateList &&
                getFormsByType(formTemplateList, "SMARTRX").map((item, _) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {errors && core?.validationMode === "ValidateAndShow" && !value && (
            <FormHelperText sx={{ marginLeft: 0, color: "error.main" }}>
              {errors}
            </FormHelperText>
          )}
        </Box>
        </>
      )}
    </>
  )
}

export default withJsonFormsControlProps(UTemplateSelectV2)
